import { AxiosError } from 'axios';
import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetChapterNumbers } from '../../../../../api/masters/masters';
import { PsNote } from '../../../../../api/model';
import { usePostPsNoteDownload } from '../../../../../api/ps-notes/ps-notes';
import { useDefaultOptions, useOptionsForBinary } from '../../../../../hooks/api';
import { executeDownload } from '../../../../../utils/download';
import * as sessionStorageUtils from '../../../../../utils/sessionStorage';
import ErrorComponent from '../../../../atoms/ErrorComponent';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import WaitComponent from '../../../../atoms/WaitComponent';
import BlobErrorModal from '../../../../modules/BlobErrorModal';
import PageNavi from '../../../../modules/PageNavi';
import SpinnerModal from '../../../../modules/SpinnerModal';

interface Props {
  productionId: number;
  psNotes: PsNote[];
  selectedCharterNumberId: string;
  setSelectedCharterNumberId: (value: string) => void;
}

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
  stWidth: string | undefined;
}

/**
 * ソート可能なテーブルヘッダー
 * @param param0
 * @returns
 */
export function SortableTh({ label, onClick, stWidth }: SortableThProps) {
  return (
    <th style={{ padding: '10px 9px', width: stWidth === undefined ? '' : stWidth }}>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

function PsNotePage({ productionId, psNotes, selectedCharterNumberId, setSelectedCharterNumberId }: Props) {
  const N_DATA_PER_PAGE = 50; // 1ページに表示する行数:50行
  const refIsFirstLoading = useRef(true); // 初回ロード中かどうか
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  // 検索条件 章
  const [downloadSpinnerModal, setDownloadSpinnerModal] = useState(false); // スピナーモーダルを表示する
  const [downloadErrorModal, setDownloadErrorModal] = useState(false); // エラーモーダルを表示する

  // 章取得
  const {
    data: Chapterdata,
    isLoading: ChapterIsLoading,
    isError: ChapterIsError,
    error: ChapterError,
  } = useGetChapterNumbers(options);
  const chapterNumberId = selectedCharterNumberId === '' ? undefined : Number(selectedCharterNumberId);

  // ページネーション
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('psNote.CurrentPage', 1));

  // ソート
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('psNote.SortInfo', { key: ['ps_no'], isDisc: true })
  );

  // ソート処理
  const allPsNote = useMemo(
    () =>
      [...(psNotes ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;

        // 配列にソートキーがセットされていない場合は何もしない
        if (sortInfo.key.length === 0) {
          return 0;
        }

        // 配列にソートキーが1つだけの場合
        if (sortInfo.key.length === 1) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }
        return ((aAny[sortInfo.key[1]] ?? '') > (bAny[sortInfo.key[1]] ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo, psNotes]
  );

  // ページネーションで一覧をフィルタリングする
  const filteredPsNote = useMemo(
    () => allPsNote.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, allPsNote]
  );

  // ソートキーを保存
  const changeSortKey = useCallback((key: string[]) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: JSON.stringify(pre.key) === JSON.stringify(key) && !pre.isDisc };
      sessionStorageUtils.set('psNote.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // ページネーションを保存
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('psNote.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  useEffect(() => {
    // ページネーションを1に戻す
    if (psNotes != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [psNotes]);

  // エクセル出力
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: downloadAsync,
    isError: downloadIsError,
    error: downloadError,
  } = usePostPsNoteDownload<AxiosError<Blob>>(optionsForBinary);

  // エクセル出力
  const download = useCallback(async () => {
    setDownloadSpinnerModal(true);
    await downloadAsync(
      { productionId, data: { chapter_number_id: chapterNumberId } },
      {
        onSuccess: (response) => {
          executeDownload(response);
          setDownloadSpinnerModal(false);
        },
      }
    );
  }, [downloadAsync, productionId, chapterNumberId]);

  // エラーの場合、エラーモーダルを非表示
  useEffect(() => {
    setDownloadSpinnerModal(false);
    setDownloadErrorModal(downloadIsError);
  }, [downloadIsError]);

  // 章一覧のデータ
  if (ChapterIsLoading) return <WaitComponent />; // ローディング
  if (ChapterIsError) return <ErrorComponent error={ChapterError} />; // エラー時

  // データがない場合
  if (Chapterdata == null || Chapterdata.data == null || Chapterdata.data.chapter_numbers == null || !psNotes) {
    return <NoDataComponent />;
  }

  return (
    <div>
      <div className="chapter-search">
        <div className="chapter-search1">
          <label htmlFor="chap-ck1">{t('章の絞り込み')}</label>
          <span className="dropdown chapter">
            <select
              onChange={(e) => {
                setSelectedCharterNumberId(e.target.value);
                sessionStorageUtils.set('psNote.ChapterNumberId', e.target.value);
              }}
              value={selectedCharterNumberId}
            >
              <option value="" key="">
                {t('')}
              </option>
              {Chapterdata.data.chapter_numbers.map((x) => (
                <option value={x.chapter_number_id} key={x.chapter_number_id}>
                  {x.chapter_name}
                </option>
              ))}
            </select>
          </span>
        </div>
        <div className="chapter-search2">
          <a href="#download" onClick={() => download()}>
            <i className="fa-solid fa-download" />
            {t('出力')}
          </a>
        </div>
      </div>
      <div className="scroll">
        <table className="table mt30">
          <tbody>
            <tr>
              {/* ラベル クリックで並び替え */}
              <SortableTh label={t('PS管理No.')} onClick={() => changeSortKey(['ps_no'])} stWidth="150px" />
              <SortableTh label={t('登録日')} onClick={() => changeSortKey(['created_at'])} stWidth="100px" />
              <SortableTh label={t('該当章')} onClick={() => changeSortKey(['chapter_name'])} stWidth="80px" />
              <SortableTh
                label={t('改訂内容（改廃理由）')}
                onClick={() => changeSortKey(['description'])}
                stWidth={undefined}
              />
              <SortableTh label={t('作成者')} onClick={() => changeSortKey(['create_user_name'])} stWidth="150px" />
            </tr>
            {/* データ表示部 */}
            {filteredPsNote.map((data) => (
              <tr key={data.ps_temporary_note_id}>
                <td style={{padding: '10px 9px'}}>{data.ps_no}</td>
                <td style={{padding: '10px 9px'}}>{data.created_at}</td>
                <td style={{padding: '10px 9px'}}>{data.chapter_name}</td>
                <td style={{padding: '10px 9px', wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                  {data.description}
                </td>
                <td style={{padding: '10px 9px'}}>{data.create_user_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ページング */}
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil(Number(psNotes.length) / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />
      {/* スピナーモーダル */}
      {downloadSpinnerModal && <SpinnerModal />}
      {/* ダウンロードエラー */}
      {downloadErrorModal && <BlobErrorModal error={downloadError} onClickOk={() => setDownloadErrorModal(false)} />}
    </div>
  );
}

export default PsNotePage;
